/**
 * Loads google maps api dynamically.
 */
class GoogleMapsApiLoader {

  #config = {};
  #state = 'initial';
  #callbacks = [];

  /**
   * Create new Google Maps API loader instance.
   */
  constructor() {
    this.setConfig();

    window.ntGoogleMapInit = () => {
      console.log('Google Maps API Loader / Ready');
      this.#state = 'ready';
      this.#callbacks.forEach(cb => cb());
    };
  }

  /**
   * Set current configurations and reset api loader state.
   *
   * @param {object} [options] Api configuration. If empty, attempts to read configurations from global variable `window.ntGoogleMapsConfig`.
   * @param {string} options.key Google Maps Api key.
   * @param {string} [options.lang] Google Maps Api language. Default is current document language.
   * @param {string} [options.version] Google Maps Api version. Default is 3.exp.
   * @param {string[]} [options.libraries] Google Maps Api libraries to load. Default is places.
   * @see {@link https://developers.google.com/maps/documentation/javascript/libraries/ Google Maps Libraries}
   */
  setConfig(options = null) {
    this.#state = 'initial';
    this.#callbacks = [];

    const config = {
      lang: document.body.dataset.country || 'en',
      key: '',
      version: '3.exp',
      libraries: [],
      ...(options || window.ntGoogleMapsConfig || {})
    };

    this.#config = config;
    console.log('Google Maps API Loader / Config set');
  }

  /**
   * Get current api configuration.
   *
   * @returns {object} Current api key, language, version and libraries.
   */
  getConfig() {
    return this.#config;
  }

  /**
   * Get current state.
   *
   * @returns {string} initial, loading or ready
   */
  getState() {
    return this.#state;
  }

  /**
   * Remove api ready event listener.
   *
   * @param {function} callback Callback to remove
   */
  unsubscribe(callback) {
    this.#callbacks = this.#callbacks.filter(cb => cb !== callback);
  }

  /**
   * Add api ready event listener.
   *
   * @param {function} callback Invoked when api has been loaded
   */
  subscribe(callback) {
    if (typeof callback === 'function') {
      this.#callbacks.push(callback);
    }
  }

  /**
   * Loads google maps api with current configuration. Configurations can be set with {@link setConfig} method.
   *
   * - If api is already loading, schedules given callback to be run after api is ready
   * - If api is already loaded, runs given callback immediately
   *
   * @param {function} [callback] Callback which is run after api is loaded.
   */
  load(callback) {
    const state = this.getState();

    // API already loaded
    if (state === 'ready') {
      if (typeof callback === 'function') {
        callback();
      }
      return;
    }

    this.subscribe(callback);

    if (state === 'loading') {
      return;
    }

    const opt = this.getConfig();

    if (!opt.key) {
      throw new Error('Google Maps Api key missing.');
    }

    // Change state to loading
    this.#state = 'loading';

    // Create script and start loading
    const query = new URLSearchParams({
      v: opt.version,
      language: opt.lang,
      libraries: opt.libraries.join(','),
      key: opt.key,
      callback: 'ntGoogleMapInit',
    });
    if (opt.libraries.length === 0) {
      query.delete('libraries');
    }

    const src = `https://maps.googleapis.com/maps/api/js?${query.toString()}`;
    const scriptEl = document.createElement('script');
    scriptEl.id = 'dynamic-script-google-maps';
    scriptEl.async = true;
    scriptEl.src = src;
    document.head.appendChild(scriptEl);

    console.log('Google Maps API Loader / Loading');
  }
}

export default new GoogleMapsApiLoader();
